import { useState } from 'react';
import { Check, Minus, Plus, ScanBarcode, Trash2 } from 'lucide-react';
import { subtotalWithin } from '@/lib/cart';
import { cn } from '@/lib/cn';
import { formatMoney, fromApi } from '@/lib/money';
import { DishArt, type Dish } from './PosDemoArt';

/**
 * A working till on the signup page.
 *
 * Not a picture of the product — the product, in miniature. Tapping a tile
 * rings it up, the tax breaks out, the total moves, and Charge clears the
 * sale. Somebody deciding whether to sign up gets to use the thing before
 * they hand over an email address, which no screenshot achieves.
 *
 * It obeys the same rules as the real till, because the rules are the point:
 *
 *   Money is INTEGER MINOR UNITS end to end (ADR-010) and formatted through
 *   lib/money. There is no float here any more than there is in CartCalculator
 *   — a demo that drifted by a paisa would be advertising the exact bug the
 *   whole codebase is built to prevent.
 *
 *   Prices INCLUDE tax, which is the Indian default and what the onboarding
 *   wizard sets. So GST is worked backwards out of the total rather than added
 *   on top, exactly as the server does it.
 */

/** Paise. A shop's actual menu, not lorem. */
const MENU: Array<{ id: number; name: string; price: number; art: Dish }> = [
  { id: 1, name: 'Masala Chai', price: 2000, art: 'chai' },
  { id: 2, name: 'Samosa', price: 2500, art: 'samosa' },
  { id: 3, name: 'Masala Dosa', price: 8000, art: 'dosa' },
  { id: 4, name: 'Vada Pav', price: 3500, art: 'vadapav' },
  { id: 5, name: 'Filter Coffee', price: 3000, art: 'coffee' },
  { id: 6, name: 'Paneer Roll', price: 12000, art: 'roll' },
];

/** Restaurant service rate. A string, because that is the shape the shared
 *  cart helper takes — tax rates arrive from the API as decimal strings. */
const GST_RATE = '5';

type Line = { id: number; name: string; price: number; qty: number };

const money = (minor: number) => formatMoney(fromApi(minor), { symbol: '₹', decimals: 2 });

export function AuthPosDemo() {
  const [lines, setLines] = useState<Line[]>([
    { id: 1, name: 'Masala Chai', price: 2000, qty: 2 },
    { id: 3, name: 'Masala Dosa', price: 8000, qty: 1 },
  ]);
  const [charged, setCharged] = useState(false);

  function add(item: (typeof MENU)[number]) {
    setCharged(false);
    setLines((current) => {
      const existing = current.find((line) => line.id === item.id);

      return existing === undefined
        ? [...current, { ...item, qty: 1 }]
        : current.map((line) => (line.id === item.id ? { ...line, qty: line.qty + 1 } : line));
    });
  }

  function step(id: number, by: number) {
    setCharged(false);
    setLines((current) =>
      current
        .map((line) => (line.id === id ? { ...line, qty: line.qty + by } : line))
        .filter((line) => line.qty > 0),
    );
  }

  /*
   * Integer arithmetic throughout, through the SAME helper the real cart uses.
   *
   * Tax is the remainder rather than its own rounded division, which is what
   * makes net + tax equal the total for every basket instead of most of them.
   */
  const total = lines.reduce((sum, line) => sum + line.price * line.qty, 0);
  const net = subtotalWithin(total, GST_RATE);
  const tax = total - net;

  const itemCount = lines.reduce((sum, line) => sum + line.qty, 0);

  return (
    <div className="auth-rise overflow-hidden rounded-2xl border border-white/15 bg-white/95 shadow-[0_28px_70px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl" style={{ animationDelay: '340ms' }}>
      {/* Chrome, so it reads as a screen rather than as a widget. */}
      <div className="flex items-center gap-2 border-b border-border bg-sunken px-4 py-2.5">
        <span className="flex gap-1.5" aria-hidden>
          <i className="h-2.5 w-2.5 rounded-full bg-critical/40" />
          <i className="h-2.5 w-2.5 rounded-full bg-warning/50" />
          <i className="h-2.5 w-2.5 rounded-full bg-good/40" />
        </span>
        <p className="ml-1 flex items-center gap-1.5 text-[11px] font-medium text-ink-secondary">
          <ScanBarcode className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          Voltra POS — Counter 1
        </p>
        <span className="ml-auto text-[11px] text-ink-muted">Try it →</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px]">
        {/* --- The menu -------------------------------------------------- */}
        <div className="grid grid-cols-2 gap-2 p-3 xl:grid-cols-3">
          {MENU.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => add(item)}
              className={cn(
                // Art flush to the top edge, text below — the same anatomy as
                // the real MenuGrid tile, which is the point of the whole
                // panel. `overflow-hidden` is what keeps the art inside the
                // rounded corner.
                'group flex flex-col overflow-hidden rounded-xl border border-border bg-surface text-left transition-all',
                'hover:-translate-y-0.5 hover:border-brand hover:shadow-md',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                'active:translate-y-0 active:scale-[0.98]',
              )}
            >
              <DishArt dish={item.art} className="block aspect-4/3 w-full" />

              <span className="flex flex-1 flex-col p-2">
                {/* Wraps rather than truncates — at the narrow end of lg the
                    column is ~90px and "Masala C…" is not a thing anybody can
                    order. The price is pushed to the bottom, so a two-line
                    name does not knock its row's prices out of alignment. */}
                <span className="line-clamp-2 text-[11px] font-medium leading-tight text-ink">
                  {item.name}
                </span>
                <span className="tabular mt-auto pt-0.5 text-[11px] text-ink-secondary">
                  {money(item.price)}
                </span>
              </span>
            </button>
          ))}
        </div>

        {/* --- The order ------------------------------------------------- */}
        <div className="flex flex-col border-t border-border bg-nav p-3 sm:border-l sm:border-t-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-nav-ink-muted">
            Order · {itemCount} item{itemCount === 1 ? '' : 's'}
          </p>

          <div className="scroll-slim-dark mt-2 min-h-26 flex-1 space-y-1.5 overflow-y-auto">
            {lines.length === 0 ? (
              <p className="pt-6 text-center text-[11px] text-nav-ink-muted">
                Tap something to ring it up
              </p>
            ) : (
              lines.map((line) => (
                <div key={line.id} className="flex items-center gap-1.5">
                  <span className="min-w-0 flex-1 truncate text-[11px] text-nav-ink">
                    {line.name}
                  </span>

                  <span className="flex items-center gap-0.5">
                    <button
                      type="button"
                      aria-label={`One fewer ${line.name}`}
                      onClick={() => step(line.id, -1)}
                      className="rounded p-0.5 text-nav-ink-muted transition-colors hover:bg-nav-hover hover:text-nav-ink"
                    >
                      {line.qty === 1 ? (
                        <Trash2 className="h-3 w-3" strokeWidth={2} />
                      ) : (
                        <Minus className="h-3 w-3" strokeWidth={2.5} />
                      )}
                    </button>
                    <span className="tabular w-4 text-center text-[11px] font-medium text-nav-ink">
                      {line.qty}
                    </span>
                    <button
                      type="button"
                      aria-label={`One more ${line.name}`}
                      onClick={() => step(line.id, 1)}
                      className="rounded p-0.5 text-nav-ink-muted transition-colors hover:bg-nav-hover hover:text-nav-ink"
                    >
                      <Plus className="h-3 w-3" strokeWidth={2.5} />
                    </button>
                  </span>

                  <span className="tabular w-14 text-right text-[11px] text-nav-ink">
                    {money(line.price * line.qty)}
                  </span>
                </div>
              ))
            )}
          </div>

          <dl className="tabular mt-2 space-y-1 border-t border-nav-border pt-2 text-[11px]">
            <div className="flex justify-between text-nav-ink-muted">
              <dt>Net</dt>
              <dd>{money(net)}</dd>
            </div>
            <div className="flex justify-between text-nav-ink-muted">
              {/* Split for display, exactly as TaxRate::splitForDisplay does
                  on a real receipt. */}
              <dt>
                CGST + SGST {GST_RATE}%
              </dt>
              <dd>{money(tax)}</dd>
            </div>
            <div className="flex justify-between pt-0.5 text-[13px] font-semibold text-nav-ink">
              <dt>Total</dt>
              <dd>{money(total)}</dd>
            </div>
          </dl>

          <button
            type="button"
            disabled={lines.length === 0}
            onClick={() => {
              setCharged(true);
              setLines([]);
            }}
            className={cn(
              'mt-2.5 flex h-9 items-center justify-center gap-1.5 rounded-lg text-[12px] font-semibold transition-colors',
              charged
                ? 'bg-good text-white'
                : 'bg-logo text-nav hover:brightness-95 disabled:pointer-events-none disabled:opacity-40',
            )}
          >
            {charged ? (
              <>
                <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden />
                Paid
              </>
            ) : (
              <>Charge {money(total)}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
